/**
 * Phase 4 — Dedicated Agent page at /dashboard/agent.
 *
 * Split layout:
 *   - Left (~50%): chat — messages, tool-call cards, input box
 *   - Right (~50%): visualisation panel — animated HeroBackground by default;
 *     tool-specific confirmation component when a tool_use needs approval;
 *     returns to the animated default after confirmation/rejection.
 *
 * Reuses /api/ai-chat/conversations/* endpoints — no new server-side work.
 * Shares no component with AIPanel (intentional; two different UX surfaces).
 *
 * Tabs at the top: Agent | Settings. Settings tab renders AgentSettingsTab.
 *
 * Optional deep-link: /dashboard/agent?topic=campaign:<id>:<reason> pre-loads
 * a new conversation with that topic as the first user turn.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ArrowUpIcon } from '@heroicons/react/24/solid';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Toggle } from '@/components/ui/toggle';
import { PageShell, PageTopRow } from '@/components/ui/page-shell';
import { AgentSettingsTab } from '@/components/agent/AgentSettingsTab';
import { WorkingView } from '@/components/agent/WorkingView';
import { getToolComponent } from '@/components/agent/tool-registry';

type TabValue = 'agent' | 'settings';

type Conversation = { id: string; title: string; updatedAt: string };
type Role = 'user' | 'assistant';
type Message = {
  id: number | string;
  role: Role;
  content: string;
  createdAt: string;
  toolCalls?: ToolCallCard[];
};
type ToolStatus = 'running' | 'completed' | 'failed' | 'needs_confirmation';
type ToolCallCard = {
  id: string;
  toolName: string;
  status: ToolStatus;
  input?: Record<string, unknown>;
  description?: string;
  result?: unknown;
};

const TOOL_LABELS: Record<string, string> = {
  list_campaigns: 'Looking up campaigns',
  get_campaign_details: 'Loading campaign details',
  create_campaign: 'Creating campaign',
  launch_campaign: 'Launching campaign',
  list_contacts: 'Searching contacts',
  navigate_to_page: 'Navigating',
  get_analytics_overview: 'Loading analytics',
  approve_email_draft: 'Approving email draft',
  pause_campaign: 'Pausing campaign',
  edit_campaign_settings: 'Editing campaign settings',
  edit_campaign_icp: 'Updating campaign ICP',
  rewrite_email_step: 'Rewriting email step',
  run_prospecting_batch: 'Running prospecting batch',
};

export default function Agent() {
  const [activeTab, setActiveTab] = useState<TabValue>('agent');

  const tabOptions = useMemo(() => ([
    { value: 'agent' as const, label: 'Agent' },
    { value: 'settings' as const, label: 'Settings' },
  ]), []);

  return (
    <PageShell>
      <PageTopRow>
        <div className="w-fit">
          <Toggle<TabValue>
            options={tabOptions}
            value={activeTab}
            onChange={setActiveTab}
            className="[&_button]:text-[0.9rem] [&_button]:!px-6"
          />
        </div>
      </PageTopRow>

      {/* The agent view runs flush, square and edge to edge like the
          campaign editor's panel, with a rule along its top; Settings keeps
          its centred boxes on the page. */}
      <div className={`flex-1 min-h-0 overflow-y-auto${activeTab === 'agent' ? ' -mx-gutter -mb-gutter border-t border-panel-border' : ''}`}>
        {activeTab === 'agent' ? <AgentChatLayout /> : <AgentSettingsTab />}
      </div>
    </PageShell>
  );
}

function AgentChatLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [activeToolCalls, setActiveToolCalls] = useState<ToolCallCard[]>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<ToolCallCard | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loadedInitialRef = useRef(false);

  useEffect(() => {
    if (!isStreaming) textareaRef.current?.focus();
  }, [isStreaming]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load conversation list on mount.
  useEffect(() => {
    fetch('/api/ai-chat/conversations', { credentials: 'include' })
      .then(r => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setConversations(data);
          if (!activeConversationId && data.length > 0) setActiveConversationId(data[0].id);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle ?topic= deep link — only fires once on initial mount.
  useEffect(() => {
    if (loadedInitialRef.current) return;
    const params = new URLSearchParams(location.search);
    const topic = params.get('topic');
    if (!topic) return;
    loadedInitialRef.current = true;

    (async () => {
      const res = await fetch('/api/ai-chat/conversations', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) return;
      const convo = await res.json() as Conversation;
      setConversations(prev => [convo, ...prev]);
      setActiveConversationId(convo.id);
      const seed = topicToPrompt(topic);
      setInput(seed);
      setTimeout(() => { void sendMessage(convo.id, seed); }, 150);
      // Remove the topic from the URL so reloads don't re-seed.
      navigate('/dashboard/agent', { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load messages for the active conversation.
  useEffect(() => {
    if (!activeConversationId) return;
    fetch(`/api/ai-chat/conversations/${activeConversationId}/messages`, { credentials: 'include' })
      .then(r => r.json())
      .then((data) => { if (Array.isArray(data)) setMessages(data); })
      .catch(() => {});
  }, [activeConversationId]);

  useEffect(() => { scrollToBottom(); }, [messages, streamingText, activeToolCalls, scrollToBottom]);

  const createConversation = async () => {
    const res = await fetch('/api/ai-chat/conversations', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return;
    const convo = await res.json() as Conversation;
    setConversations(prev => [convo, ...prev]);
    setActiveConversationId(convo.id);
    setMessages([]);
    setPendingConfirmation(null);
  };

  const sendMessage = async (targetConversationId?: string, overrideContent?: string) => {
    const convoId = targetConversationId || activeConversationId;
    const content = (overrideContent ?? input).trim();
    if (!convoId || !content || isStreaming) return;

    // Optimistic user message render.
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    }]);
    setInput('');
    setIsStreaming(true);
    setStreamingText('');
    setActiveToolCalls([]);

    const toolCalls: ToolCallCard[] = [];
    let fullText = '';

    try {
      const res = await fetch(`/api/ai-chat/conversations/${convoId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, uiContext: { currentPage: '/dashboard/agent' } }),
      });
      if (!res.ok || !res.body) throw new Error(await res.text().catch(() => 'stream failed'));

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'token') {
              fullText += data.text;
              setStreamingText(fullText);
            } else if (data.type === 'tool_start') {
              const tc: ToolCallCard = { id: data.toolCallId, toolName: data.toolName, status: 'running' };
              toolCalls.push(tc);
              setActiveToolCalls([...toolCalls]);
            } else if (data.type === 'tool_result') {
              const existing = toolCalls.find(t => t.id === data.toolCallId);
              if (existing) {
                existing.status = data.result?.success ? 'completed' : 'failed';
                existing.result = data.result;
                setActiveToolCalls([...toolCalls]);
              }
              if (data.clientAction) handleClientAction(data.clientAction);
            } else if (data.type === 'tool_needs_confirmation') {
              const tc: ToolCallCard = {
                id: data.toolCallId,
                toolName: data.toolName,
                status: 'needs_confirmation',
                input: data.input,
                description: data.description,
              };
              toolCalls.push(tc);
              setActiveToolCalls([...toolCalls]);
              setPendingConfirmation(tc);
            } else if (data.type === 'done') {
              setMessages(prev => [...prev, {
                id: data.messageId,
                role: 'assistant',
                content: fullText,
                createdAt: new Date().toISOString(),
                toolCalls: toolCalls.length > 0 ? [...toolCalls] : undefined,
              }]);
              setStreamingText('');
              setActiveToolCalls([]);
              if (data.title) {
                setConversations(prev => prev.map(c => c.id === convoId ? { ...c, title: data.title } : c));
              }
            } else if (data.type === 'error') {
              toast.error(`Agent error: ${data.message ?? 'unknown'}`);
              setStreamingText('');
            }
          } catch { /* ignore malformed chunk */ }
        }
      }
    } catch (err) {
      toast.error(`Chat failed: ${(err as Error).message}`);
      setStreamingText('');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleClientAction = (action: { type: string; path?: string; message?: string }) => {
    if (action.type === 'navigate' && action.path) navigate(action.path);
    else if (action.type === 'show_toast' && action.message) toast(action.message);
  };

  const handleConfirmTool = (modifiedInput: Record<string, unknown>) => {
    if (!pendingConfirmation) return;
    const toolName = pendingConfirmation.toolName;
    setPendingConfirmation(null);
    const followUp = `Yes, proceed with \`${toolName}\`. Use this input:\n\n\`\`\`json\n${JSON.stringify(modifiedInput, null, 2)}\n\`\`\``;
    void sendMessage(undefined, followUp);
  };

  const handleRejectTool = () => {
    if (!pendingConfirmation) return;
    setPendingConfirmation(null);
    void sendMessage(undefined, `No, cancel \`${pendingConfirmation.toolName}\`.`);
  };

  const ToolComponent: FC<Parameters<typeof getToolComponent>[0] extends string ? any : any> | null =
    pendingConfirmation ? getToolComponent(pendingConfirmation.toolName) : null;

  return (
    <div className="flex flex-row-reverse h-full">
      {/* LEFT — chat */}
      <div className="flex-[1_1_50%] min-w-[360px] flex flex-col bg-card overflow-hidden relative">
        <div className="absolute top-header-gap left-page-inset right-page-inset z-50 flex items-center gap-3">
          <Button variant="secondary" onClick={createConversation}>New</Button>
          {conversations.length > 0 ? (
            <Select
              value={activeConversationId || undefined}
              onValueChange={(id) => setActiveConversationId(id)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select conversation" />
              </SelectTrigger>
              <SelectContent>
                {conversations.map(conv => (
                  <SelectItem key={conv.id} value={conv.id}>
                    {conv.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex-1" />
          )}
        </div>

        <div className="relative flex-1 min-h-0">
          <div className="h-28 bg-gradient-to-b from-card via-card/80 to-card/0 absolute top-0 left-0 right-[8px] z-[9] pointer-events-none" />
          <div className="h-56 bg-gradient-to-b from-card/0 to-card absolute bottom-0 left-0 right-[8px] z-[8] pointer-events-none" />
          {/* Overlay rather than a scroll-area child, so it centres in the space
              above the floating input regardless of the scroll padding. */}
          {messages.length === 0 && !streamingText && (
            <div className="absolute inset-0 pt-14 pb-36 px-page-inset flex items-center justify-center text-center text-foreground text-sm pointer-events-none">
              Ask the agent to manage your campaigns, draft prospects, or adjust settings.
            </div>
          )}
          <div className="absolute inset-0 overflow-y-auto px-page-inset pt-24 pb-52 space-y-3">
            {messages.map(m => (
              <MessageBubble key={m.id} message={m} />
            ))}

            {activeToolCalls.length > 0 && <ToolCallList calls={activeToolCalls} />}

            {streamingText && (
              <MessageBubble message={{
                id: 'streaming',
                role: 'assistant',
                content: streamingText,
                createdAt: new Date().toISOString(),
              }} />
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); void sendMessage(); }}
          className="absolute left-page-inset right-page-inset bottom-4 z-50"
        >
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void sendMessage();
                }
              }}
              placeholder="Ask the agent…"
              className="min-h-[120px] resize-none bg-card shadow-md pr-20 overflow-y-auto"
              disabled={isStreaming || !activeConversationId}
            />
            {(input.trim() || isStreaming) && (
              <Button
                type="submit"
                variant="primary"
                className="absolute bottom-2 right-2"
                disabled={isStreaming || !input.trim() || !activeConversationId}
              >
                {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpIcon className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* RIGHT — Working view (activity feed + tool-confirmation modal overlay) */}
      <div className="flex-[1_1_50%] min-w-[320px] bg-card border-r border-panel-border overflow-hidden relative">
        <WorkingView
          modal={pendingConfirmation ? (
            ToolComponent ? (
              <ToolComponent
                input={pendingConfirmation.input ?? {}}
                description={pendingConfirmation.description}
                onConfirm={handleConfirmTool}
                onReject={handleRejectTool}
              />
            ) : (
              <GenericConfirmation
                toolName={pendingConfirmation.toolName}
                input={pendingConfirmation.input ?? {}}
                description={pendingConfirmation.description}
                onConfirm={handleConfirmTool}
                onReject={handleRejectTool}
              />
            )
          ) : null}
        />
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
        isUser ? 'bg-surface-bubble text-surface-bubble-foreground' : 'text-foreground'
      }`}>
        {message.content}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <ToolCallList calls={message.toolCalls} compact />
        )}
      </div>
    </div>
  );
}

function ToolCallList({ calls, compact = false }: { calls: ToolCallCard[]; compact?: boolean }) {
  return (
    <div className={compact ? 'mt-2 space-y-1' : 'space-y-1.5 my-2'}>
      {calls.map(tc => (
        <div key={tc.id} className="flex items-center gap-2 text-xs text-muted-foreground">
          {tc.status === 'running' && (
            <span className="inline-block w-3 h-3 border-2 border-info-on-dark border-t-transparent rounded-full animate-spin" />
          )}
          {tc.status === 'completed' && (
            <span className="inline-flex items-center justify-center w-4 h-4 bg-success-surface-strong rounded-full">
              <CheckIcon className="w-2.5 h-2.5 text-success-foreground" />
            </span>
          )}
          {tc.status === 'failed' && (
            <XCircleIcon className="h-3.5 w-3.5 text-danger-text" />
          )}
          {tc.status === 'needs_confirmation' && (
            <span className="inline-block w-3 h-3 bg-warning-solid-soft rounded-full" />
          )}
          <span>{TOOL_LABELS[tc.toolName] || tc.toolName}</span>
        </div>
      ))}
    </div>
  );
}

function GenericConfirmation({
  toolName, input, description, onConfirm, onReject,
}: {
  toolName: string;
  input: Record<string, unknown>;
  description?: string;
  onConfirm: (input: Record<string, unknown>) => void;
  onReject: () => void;
}) {
  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-lg">
        <h2 className="text-lg font-semibold mb-2">
          {TOOL_LABELS[toolName] || toolName}
        </h2>
        <p className="text-sm text-muted-foreground mb-3">
          {description || 'This action needs your approval before the agent runs it.'}
        </p>
        <pre className="text-xs bg-surface-sunken border border-border rounded p-3 max-h-60 overflow-y-auto mb-4">
          {JSON.stringify(input, null, 2)}
        </pre>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onReject}>Cancel</Button>
          <Button variant="primary" onClick={() => onConfirm(input)}>Confirm</Button>
        </div>
      </div>
    </div>
  );
}

function topicToPrompt(topic: string): string {
  // Topic format: "campaign:<id>:<reason>" (e.g. "campaign:abc-123:no-replies")
  const parts = topic.split(':');
  if (parts[0] === 'campaign' && parts[1] && parts[2]) {
    const [, campaignId, reason] = parts;
    const humanReason = reason.replace(/-/g, ' ');
    return `I got a notification about campaign ${campaignId} — ${humanReason}. What's going on and what should we do?`;
  }
  return `Topic: ${topic}`;
}
